/**
 * UserProfile Component
 *
 * Main container component for the User Profile page that displays comprehensive
 * user information including personal details, tool/role assignments, and navigation.
 *
 * Features:
 * - Retrieves email from URL query parameter (?email=...)
 * - Fetches user profile data from GetUserProfile API endpoint
 * - Displays SectionHeader with "PROFILE" title and blue divider
 * - Renders user information panel with all required fields
 * - Email field as clickable mailto: hyperlink
 * - JobTitle displays "N/A" if empty or null
 * - LastLogin formatted as MM/DD/YYYY or "N/A" for invalid dates
 * - "Manage myRSM access" button opens UserManagementAssignmentModal for editing tool-role assignments
 * - Embeds UserProfileAccessTable component for tool/role display
 * - "View all users" link for navigation back to user listing
 * - Comprehensive loading and error state handling
 * - Full accessibility compliance with ARIA labels
 * - Responsive design with theme-aware breakpoints
 * - Success/error toast notifications for assignment management operations
 *
 * Integration Points:
 * - API: GET /api/v1/ClientAdmin/GetUserProfile?emailAddress={email}
 * - Modal: UserManagementAssignmentModal for managing user assignments
 * - Redux: UserProfileAccessTable reads from userManagementSlice
 * - Routing: Accessed via /user-management/userprofile?email={email}
 * - i18n: All text from UserManagement.Profile.* translation keys
 *
 * @component
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Grid,
  Link,
} from '@mui/material'
import { UserProfileViewModel } from '@rsmus/ecp-userservice'
import { useDispatch, useSelector } from 'react-redux'
import { ArrowLeftIcon } from '@rsmus/myrsm-icons'
import SectionHeader from '../layouts/SectionHeader/SectionHeader'
import UserProfileAccessTable from './UserProfileAccessTable'
import tokens from '../../styles/tokens.json'
import { Styles } from '../../types'
import api from '../../api'
import {
  getSelectedUserProfileData,
  setSelectedUserProfileData,
} from '../../store/userManagement/userManagementProfileSlice'
import { EmailIcon } from '../../rsmCoreComponents/components/icons'
import UserManagementAssignmentModal from './UserManagementAssignmentModal'
import CustomSuccessAlert from '../forms/Alert/CustomSuccessAlert/CustomSuccessAlert'
import CustomErrorAlert from '../forms/Alert/CustomErrorAlert/CustomErrorAlert'

/**
 * Component styles following established patterns from Home.tsx and UserManagementTable.tsx
 * Uses theme-aware responsive breakpoints and RSM design tokens
 */
const styles: Styles = {
  // Main page container with responsive padding following Home.tsx pattern
  mainContainer: (theme) => ({
    padding: '2.5rem 1rem',
    backgroundColor: theme.palette.common.white,
    [theme.breakpoints.only('tablet')]: {
      paddingRight: '2rem',
      paddingLeft: '2rem',
    },
    [theme.breakpoints.up('desktop')]: {
      paddingRight: '6.5rem',
      paddingLeft: '6.5rem',
    },
  }),

  // Loading state container - centered spinner with message
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '25rem',
    gap: 2,
  },

  // Loading text styling
  loadingText: {
    fontFamily: 'Prelo-Book, Arial, sans-serif',
    color: tokens.colors.rsmText.primary,
    fontSize: '1rem',
    marginTop: '1rem',
  },

  // Error state container - centered with padding
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '25rem',
    padding: '2rem',
    gap: 2,
  },

  // Error title styling
  errorTitle: {
    fontFamily: 'Prelo-Bold, Arial, sans-serif',
    color: tokens.colors.rsmRed.secondary,
    fontSize: '1.5rem',
    marginBottom: '1rem',
  },

  // Error message text
  errorText: {
    fontFamily: 'Prelo-Book, Arial, sans-serif',
    color: tokens.colors.rsmText.primary,
    fontSize: '1rem',
    textAlign: 'center',
    marginBottom: '1.5rem',
    maxWidth: '37.5rem',
  },

  // Grid container for user information fields
  infoGrid: {
    marginBottom: '2rem',
  },

  // Field label styling (NAME, EMAIL, JOB TITLE, etc.)
  fieldLabel: {
    fontFamily: 'Prelo-Bold, Arial, sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: tokens.colors.rsmText.primary,
    fontWeight: 700,
    marginRight: '0.5rem',
  },

  // Field value styling
  fieldValue: {
    fontFamily: 'Prelo-Book, Arial, sans-serif',
    fontSize: '1rem',
    lineHeight: '1.5rem',
    color: tokens.colors.rsmText.primary,
  },

  // Email link styling (mailto: hyperlink)
  emailLink: (theme) => ({
    color: theme.palette.secondary.main,
    fontWeight: '500',
    textDecoration: 'underline',
    display: 'flex',
    fontFamily: 'Prelo-Book, Arial, sans-serif',
    fontSize: '1rem',
    cursor: 'pointer',
  }),

  // Button container for Manage Access button
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  // Manage myRSM access button styling
  manageAccessButton: {
    fontSize: '1rem',
    height: '2.75rem', // WCAG touch target requirement
    paddingX: 3,
    fontWeight: 600,
    fontFamily: 'Prelo-Light',
  },

  // Tool Access section container
  accessSection: {
    marginTop: '2rem',
    marginBottom: '2rem',
  },

  // Navigation link container
  navigationContainer: {
    marginLeft: '6rem',
    marginBottom: '2rem',
  },
  link: {
    display: 'block',
    textDecoration: 'underline',
    marginLeft: '0.5rem',
  },
  textButton: (theme) => ({
    color: theme.palette.secondary.main,
    fontWeight: 500,
    fontSize: '1rem',
    lineHeight: '1.5rem',
    justifyContent: 'start',
    verticalAlign: 'top',
    padding: 0,
  }),
}

type ExtendedUserProfileViewModel = UserProfileViewModel & {
  firstName?: string
  lastName?: string
  name?: string
}

const deriveNameParts = (fullName?: string) => {
  if (!fullName) return { firstName: '', lastName: '' }

  if (fullName.includes(',')) {
    const [last, first] = fullName.split(',').map((part) => part.trim())
    return { firstName: first || '', lastName: last || '' }
  }

  const parts = fullName.split(' ').filter(Boolean)
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' '),
  }
}

const UserProfile = () => {
  // Hooks for internationalization, navigation, and URL parameters
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedUserProfileData = useSelector(getSelectedUserProfileData)

  // Component state management
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [profileData, setProfileData] = useState<UserProfileViewModel | null>(
    null,
  )
  const [openManageModal, setOpenManageModal] = useState<boolean>(false)
  const [showSuccessToast, setShowSuccessToast] = useState<boolean>(false)
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false)

  /**
   * Format date to MM/DD/YYYY format or return null for invalid dates
   * Follows the pattern from UserManagementTable.formatLastLogin
   *
   * Handles edge cases:
   * - Null or undefined values
   * - Invalid date strings
   * - DateTime.MinValue (year 1 or year < 1900)
   * - Invalid Date objects
   *
   * @param dateValue - Date to format (Date object or ISO string)
   * @returns Formatted date string (MM/DD/YYYY) or null for invalid dates
   */
  const formatDate = (
    dateValue: Date | string | null | undefined,
  ): string | null => {
    if (!dateValue) return null

    try {
      // Convert string to Date if necessary
      const date =
        typeof dateValue === 'string' ? new Date(dateValue) : dateValue

      // Validate date: check for invalid dates, year 1 (DateTime.MinValue), or years before 1900
      if (
        !date ||
        Number.isNaN(date.getTime()) ||
        date.getTime() === 0 ||
        date.getFullYear() === 1 ||
        date.getFullYear() < 1900
      ) {
        return null
      }

      // Format as MM/DD/YYYY (US format)
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const year = date.getFullYear()

      return `${month}/${day}/${year}`
    } catch (err) {
      // Handle any parsing errors
      // eslint-disable-next-line no-console
      console.error('Error formatting date:', err)
      return null
    }
  }

  useEffect(() => {
    const fetchUserProfile = async () => {
      // Validate email parameter exists
      if (!selectedUserProfileData.userEmail) {
        setError(
          t('UserManagement.Profile.ErrorNoEmail', 'No user email provided'),
        )
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Make GET request to fetch user profile
        const response = await api.user.clientAdmin_GetUserProfile(
          selectedUserProfileData.userEmail,
        )

        // Handle non-200 HTTP responses
        if (!response.statusCode || response.statusCode !== 200) {
          throw new Error(
            t(
              'UserManagement.Profile.ErrorFetchFailed',
              `Failed to load user profile (HTTP ${response.statusCode})`,
            ),
          )
        }

        // Validate response structure
        if (response.statusCode === 200 && response.data) {
          setProfileData(response.data)
        } else if (response.error?.exceptionMessage) {
          throw new Error(response.error.exceptionMessage)
        } else {
          throw new Error(
            t(
              'UserManagement.Profile.ErrorNoData',
              'No profile data returned from API',
            ),
          )
        }
      } catch (err: unknown) {
        // Handle all errors (network, parsing, API errors)
        // eslint-disable-next-line no-console
        console.error('Error fetching user profile:', err)
        const errorMessage =
          err instanceof Error
            ? err.message
            : t(
                'UserManagement.Profile.ErrorGeneric',
                'Failed to load user profile',
              )
        setError(errorMessage)
      } finally {
        // Always clear loading state
        setLoading(false)
      }
    }

    // Execute fetch on mount and when email changes
    fetchUserProfile()
  }, [selectedUserProfileData.userEmail, t])

  useEffect(() => {
    // Prevent user from accessing this page directly.
    if (
      !selectedUserProfileData.userEmail ||
      !selectedUserProfileData.userName
    ) {
      navigate('/user-management')
    }

    // unmount useEffect to reset store values when navigating away from this page
    return () => {
      dispatch(setSelectedUserProfileData({ userEmail: '', userName: '' }))
      setError(null)
    }
  }, [])

  /**
   * Handle navigation back to user management listing
   * Uses React Router's navigate function to return to /user-management route
   * State preservation: User's previous filters/search should be maintained by UserManagementLayout
   */
  const handleViewAllUsers = () => {
    dispatch(setSelectedUserProfileData({ userEmail: '', userName: '' }))
    navigate('/user-management')
  }

  /**
   * Handle successful assignment update
   * Closes the modal and displays success toast notification
   * Toast auto-dismisses after default timeout
   */
  const handleModalSuccess = () => {
    setOpenManageModal(false)
    setShowSuccessToast(true)
  }

  /**
   * Handle assignment update error
   * Closes the modal and displays error toast notification with support ticket link
   * Toast remains visible until user dismisses it
   */
  const handleModalError = () => {
    setOpenManageModal(false)
    setShowErrorToast(true)
  }
  // Derive first and last name for modal from profile data or selected user data
  const extendedProfile = profileData as ExtendedUserProfileViewModel | null
  const parsedName = deriveNameParts(
    extendedProfile?.name || selectedUserProfileData.userName,
  )
  const modalFirstName = extendedProfile?.firstName || parsedName.firstName
  const modalLastName = extendedProfile?.lastName || parsedName.lastName

  /**
   * Render loading state
   * Displays centered spinner with loading message while data is being fetched
   */
  if (loading) {
    return (
      <Box
        component="main"
        sx={styles.mainContainer}
        data-testid="Page_UserProfile_Loading">
        <Box sx={styles.loadingContainer}>
          <CircularProgress
            size={60}
            thickness={4}
            data-testid="Loading_UserProfile"
            sx={{ color: tokens.colors.rsmBlue.primary }}
          />
          <Typography variant="body1" sx={styles.loadingText}>
            {t('Common.Loading', 'Loading...')}
          </Typography>
        </Box>
      </Box>
    )
  }

  /**
   * Render error state
   * Displays error message with option to return to user listing
   * Handles both explicit errors and missing profile data scenarios
   */
  if (error || !profileData) {
    return (
      <Box
        component="main"
        sx={styles.mainContainer}
        data-testid="Page_UserProfile_Error">
        <Box sx={styles.errorContainer}>
          <Typography
            variant="h5"
            component="h1"
            sx={styles.errorTitle}
            data-testid="Lbl_Error_Title">
            {t('Common.Error', 'Error')}
          </Typography>
          <Typography
            variant="body1"
            sx={styles.errorText}
            data-testid="Lbl_Error_Message">
            {error ||
              t(
                'UserManagement.Profile.ErrorNoData',
                'Unable to load user profile',
              )}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleViewAllUsers}
            data-testid="Btn_ReturnToListing"
            sx={styles.manageAccessButton}>
            {t('UserManagement.Profile.ViewAllUsers', 'View all users')}
          </Button>
        </Box>
      </Box>
    )
  }

  /**
   * Render main profile content
   * Displays complete user profile with all sections:
   * - Page header with PROFILE title and blue divider line (SectionHeader)
   * - Basic Information panel with user details grid
   * - Manage myRSM access button (placeholder, no onClick functionality)
   * - Tool Access section with UserProfileAccessTable
   * - View all users navigation link
   */
  return (
    <>
      <Box sx={styles.navigationContainer}>
        <Button
          data-testid="Btn_UserProfile_ViewAllUsers"
          onClick={handleViewAllUsers}
          sx={styles.textButton}
          role="link">
          <ArrowLeftIcon />
          <Box component="span" sx={styles.link}>
            {t('UserManagement.Profile.ViewAllUsers', 'View all users')}
          </Box>
        </Button>
      </Box>
      <Box
        component="main"
        sx={styles.mainContainer}
        data-testid="Page_UserProfile"
        aria-label={t(
          'UserManagement.Profile.AccessibilityLabels.ProfileHeader',
          'User profile information',
        )}>
        {/* 
        Page Header Section
        Uses SectionHeader component following Home.tsx pattern
        Displays "PROFILE" title with blue horizontal line that extends to the right
      */}
        <SectionHeader
          title={t('UserManagement.Profile.Title', 'PROFILE')}
          testId="Lbl_UserProfile_Header"
        />

        {/* 
        Basic Information Panel
        Displays user details in a responsive grid layout
        Fields: Name, Email (mailto link), Job Title (N/A if empty), Company, Status, Last Login (formatted or N/A)
      */}
        <Box data-testid="Section_UserInformation">
          <Grid container spacing={3} sx={styles.infoGrid}>
            {/* Name Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="body1"
                component="div"
                sx={styles.fieldLabel}
                data-testid="Lbl_UserName">
                {selectedUserProfileData.userName}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={styles.fieldValue}
                data-testid="Lbl_JobTitle">
                {profileData.jobTitle && profileData.jobTitle.trim()
                  ? profileData.jobTitle
                  : t('UserManagement.Profile.NoJobTitle', 'N/A')}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={styles.fieldValue}
                data-testid="Lbl_Company">
                {profileData.companyName}
              </Typography>
              <Typography
                variant="body1"
                component="div"
                sx={styles.fieldValue}>
                <Box
                  component="a"
                  href={`mailto:${selectedUserProfileData.userEmail}`}
                  sx={styles.emailLink}
                  data-testid="Lnk_UserEmail"
                  aria-label={`Send email to ${selectedUserProfileData.userEmail}`}>
                  <EmailIcon />
                  <Box component="span" sx={{ marginLeft: '0.5rem' }}>
                    {selectedUserProfileData.userEmail}
                  </Box>
                </Box>
              </Typography>
            </Grid>
            {/* Status Field */}
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex' }}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={styles.fieldLabel}>
                  {t('UserManagement.Profile.Status', 'Status')}:
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={styles.fieldValue}
                  data-testid="Lbl_Status">
                  {profileData.status}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={styles.fieldLabel}>
                  {t('UserManagement.Profile.LastLogin', 'Last Login')}:
                </Typography>
                <Typography
                  variant="body1"
                  component="div"
                  sx={styles.fieldValue}
                  data-testid="Lbl_LastLogin">
                  {formatDate(profileData.lastLogin) ||
                    t('UserManagement.Profile.NoLastLogin', 'N/A')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={styles.buttonContainer}>
                <Button
                  variant="contained"
                  color="primary"
                  data-testid="Btn_UserProfile_ManageAccess"
                  sx={styles.manageAccessButton}
                  onClick={() => setOpenManageModal(true)}
                  aria-label={t(
                    'UserManagement.Profile.ManageAccess',
                    'Manage myRSM access',
                  )}>
                  {t(
                    'UserManagement.Profile.ManageAccess',
                    'Manage myRSM access',
                  )}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* 
        Tool Access Section
        Displays user's tool and role assignments using UserProfileAccessTable component
        Table filters data from Redux store by userEmail and sorts alphabetically
      */}
        <Box sx={styles.accessSection} data-testid="Section_ToolAccess">
          <UserProfileAccessTable
            userEmail={selectedUserProfileData.userEmail}
          />
        </Box>
      </Box>

      {/* 
        UserManagementAssignmentModal
        Modal for managing user's tool-role assignments
        Opened when "Manage myRSM access" button is clicked
      */}
      <UserManagementAssignmentModal
        open={openManageModal}
        onClose={() => setOpenManageModal(false)}
        onSuccess={handleModalSuccess}
        onError={handleModalError}
        userEmail={selectedUserProfileData.userEmail || ''}
        firstName={modalFirstName}
        lastName={modalLastName}
      />

      {/* 
        Success Toast Notification
        Displays when user assignments are successfully updated
      */}
      <CustomSuccessAlert
        testId="Banner_UserProfile_ManageAccessSuccess"
        header="UserManagement.ManageUser.SuccessHeader"
        message="UserManagement.ManageUser.SuccessMessage"
        open={showSuccessToast}
        close={() => setShowSuccessToast(false)}
      />

      {/* 
        Error Toast Notification
        Displays when assignment update fails with support ticket link
      */}
      <CustomErrorAlert
        testId="Banner_UserProfile_ManageAccessError"
        header="Alert.GenericErrorHeader"
        messageElement={
          <Trans
            i18nKey="UserManagement.ManageUser.ErrorMessage"
            defaults={t('UserManagement.ManageUser.ErrorMessage')}
            components={[
              <Link
                sx={(theme) => ({
                  color: theme.palette.secondary.main,
                  textDecoration: 'underline',
                })}
                href="/contact-us/support-page"
                target="_self"
                rel="noopener noreferrer"
              />,
            ]}
          />
        }
        open={showErrorToast}
        close={() => setShowErrorToast(false)}
        disableAutoHide
      />
    </>
  )
}

export default UserProfile
